import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UserInfo from "./AllAvailabilities";

// ✅ USE ACTUAL EXPORT NAMES FROM SLICE
import {
  getOtherMedicalAvailability,
  saveOtherMedicalAvailability,
  updateSingleDay,
  updateSingleSlot,
  clearWeek,
  deleteAvailability,
} from "../../redux/apis/Availability/alternativeMedicalAvailabilitySlice";

// ================= CONFIG =================
const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const defaultSessions = [
  { name: "First Half", startTime: "09:00", endTime: "13:00", status: "open" },
  { name: "Second Half", startTime: "14:00", endTime: "18:00", status: "open" },
];

// ================= DATE =================
const formatDate = (d) => d.toISOString().split("T")[0];

// ✅ TODAY + NEXT 6 DAYS ONLY
const getWeek = () => {
  const base = new Date();
  base.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);

    return {
      day: days[d.getDay()],
      date: formatDate(d),
      isHoliday: false,
      status: "open",
      sessions: defaultSessions.map((s) => ({ ...s })),
    };
  });
};

// ================= COMPONENT =================
const AlternativeMedicalAvailabilitySlots = () => {
  const dispatch = useDispatch();

  const { single } = useSelector((state) => state.alternativeAvailability);

  const token = useSelector((s) => s.auth.token);

  const [weekly, setWeekly] = useState(getWeek());

  // ================= LOAD =================
  useEffect(() => {
    if (token) {
      dispatch(getOtherMedicalAvailability({ token }));
    }
  }, [token, dispatch]);

  // ================= MAP BACKEND =================
  useEffect(() => {
    if (single?.weeklyAvailability?.length) {
      setWeekly(
        single.weeklyAvailability.map((d) => ({
          day: d.day,
          date: d.date,
          isHoliday: d.isHoliday,
          status: d.status || "open",
          sessions: defaultSessions.map((s) => {
            const match = d.sessions?.find((x) => x.name === s.name);
            return match ? { ...s, ...match } : { ...s, status: "closed" };
          }),
        })),
      );
    }
  }, [single]);

  // ================= UPDATE DAY =================
  const updateDay = (i, field, value) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (field === "date") {
      const selected = new Date(value);
      if (selected < today) {
        alert("❌ Cannot select past date");
        return;
      }
    }

    const copy = [...weekly];
    copy[i][field] = value;
    setWeekly(copy);
  };

  // ================= UPDATE SLOT =================
  const updateSlot = (i, si, field, value) => {
    const copy = [...weekly];
    const slot = copy[i].sessions[si];

    const updated = { ...slot, [field]: value };

    if (updated.startTime >= updated.endTime) {
      alert("Start time must be before end time");
      return;
    }

    copy[i].sessions[si] = updated;
    setWeekly(copy);
  };

  // ================= TOGGLE DAY =================
  const toggleDayStatus = (i) => {
    const copy = [...weekly];

    copy[i].status = copy[i].status === "open" ? "closed" : "open";

    if (copy[i].status === "closed") {
      copy[i].sessions.forEach((s) => (s.status = "closed"));
    }

    setWeekly(copy);
  };

  // ================= TOGGLE SLOT =================
  const toggleSlotStatus = (i, si) => {
    const copy = [...weekly];

    copy[i].sessions[si].status =
      copy[i].sessions[si].status === "open" ? "closed" : "open";

    setWeekly(copy);
  };

  // ================= SAVE WEEK =================
  const saveWeek = () => {
    const payload = {
      weeklyAvailability: weekly.map((d) => ({
        day: d.day,
        date: d.date,
        isHoliday: d.isHoliday,
        status: d.status,
        sessions: d.sessions.map((s) => ({
          name: s.name,
          startTime: s.startTime,
          endTime: s.endTime,
          status: s.status,
        })),
      })),
    };

    dispatch(saveOtherMedicalAvailability({ data: payload, token }));
  };

  // ================= SAVE DAY =================
  const saveDay = (day) => {
    dispatch(updateSingleDay({ data: day, token }));
  };

  // ================= SAVE SLOT =================
  const saveSlot = (day, session) => {
    dispatch(
      updateSingleSlot({
        data: {
          date: day.date,
          name: session.name,
          startTime: session.startTime,
          endTime: session.endTime,
          status: session.status,
        },
        token,
      }),
    );
  };

  // ================= CLEAR =================
  const clearWeekHandler = () => {
    dispatch(clearWeek({ token }));
    setWeekly(getWeek());
  };

  // ================= DELETE =================
  const deleteAllHandler = () => {
    dispatch(deleteAvailability({ token }));
    setWeekly(getWeek());
  };

  // ================= UI =================
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">
        🏥 Alternative Medical Availability Slots
      </h2>

      <UserInfo />

      {/* ACTIONS */}
      <div className="flex gap-3 mb-4">
        <button
          onClick={saveWeek}
          className="bg-green-600 text-white px-3 py-2 rounded"
        >
          Save Week
        </button>

        <button
          onClick={clearWeekHandler}
          className="bg-yellow-500 text-white px-3 py-2 rounded"
        >
          Clear Week
        </button>

        <button
          onClick={deleteAllHandler}
          className="bg-red-600 text-white px-3 py-2 rounded"
        >
          Delete All
        </button>
      </div>

      {/* WEEK */}
      {weekly.map((d, i) => (
        <div key={d.date} className="border p-4 mb-4 rounded bg-white">
          {/* HEADER */}
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-bold">{d.day}</h3>

              <input
                type="date"
                value={d.date}
                min={formatDate(new Date())}
                onChange={(e) => updateDay(i, "date", e.target.value)}
                className="border p-1"
              />

              <p>Status: {d.status}</p>
            </div>

            <button
              onClick={() => toggleDayStatus(i)}
              className={`px-3 py-1 rounded text-white ${
                d.status === "open" ? "bg-green-600" : "bg-gray-600"
              }`}
            >
              {d.status === "open" ? "Close Day" : "Open Day"}
            </button>
          </div>

          {/* SESSIONS */}
          {d.status === "open" &&
            d.sessions.map((s, si) => (
              <div key={si} className="flex gap-2 mt-3 items-center">
                <span className="w-32">{s.name}</span>

                <input
                  type="time"
                  value={s.startTime}
                  onChange={(e) =>
                    updateSlot(i, si, "startTime", e.target.value)
                  }
                  className="border p-1"
                />

                <input
                  type="time"
                  value={s.endTime}
                  onChange={(e) => updateSlot(i, si, "endTime", e.target.value)}
                  className="border p-1"
                />

                <button
                  onClick={() => toggleSlotStatus(i, si)}
                  className={`px-2 py-1 rounded text-white ${
                    s.status === "open" ? "bg-green-500" : "bg-red-500"
                  }`}
                >
                  {s.status === "open" ? "Close" : "Open"}
                </button>

                <button
                  onClick={() => saveSlot(d, s)}
                  className="bg-indigo-600 text-white px-2 py-1 rounded"
                >
                  Save Slot
                </button>
              </div>
            ))}

          {/* SAVE DAY */}
          <button
            onClick={() => saveDay(d)}
            className="mt-3 bg-blue-600 text-white px-3 py-1 rounded"
          >
            Save Day
          </button>
        </div>
      ))}
    </div>
  );
};

export default AlternativeMedicalAvailabilitySlots;
